import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  ALPHABET_MODULES,
  moduleChars,
  staticChars,
  UNLOCK_PCT,
} from "@/lib/alphabetModules";
import {
  HelpCircle,
  Lock,
  Check,
  ArrowRight,
  ArrowLeft,
  X,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { t } from "@/i18n";

/* ÉCHELLE AUTOMATIQUE */
const U_MIN = 6; /* Augmenté pour empêcher le texte de devenir microscopique */
const U_MAX = 13;
const FILL = 0.99;
const PASSES = 7;

export default function AlphabetModules() {
  const navigate = useNavigate();
  const [mastered, setMastered] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [gate, setGate] = useState(null);
  const [hasSwiped, setHasSwiped] = useState(false);

  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const gridRef = useRef(null);
  const lastU = useRef(9);

  const fit = useCallback(() => {
    const card = cardRef.current;
    const inner = innerRef.current;
    if (!card || !inner) return;

    const avail = card.clientHeight;
    const width = card.clientWidth;
    if (!avail || !width) return;

    const uMax = Math.min(U_MAX, width / 20);
    const target = avail * FILL;

    let u = Math.min(Math.max(lastU.current, U_MIN), uMax);

    for (let pass = 0; pass < PASSES; pass++) {
      inner.style.setProperty("--u", `${u}px`);
      const measured = inner.scrollHeight;
      if (!measured) break;

      const ratio = target / measured;
      if (ratio > 0.99 && ratio < 1.01) break;

      const nextU = Math.min(Math.max(u * ratio, U_MIN), uMax);
      if (Math.abs(nextU - u) < 0.05) {
        u = nextU;
        break;
      }
      u = nextU;
    }

    inner.style.setProperty("--u", `${u}px`);
    lastU.current = u;
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [fit]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(card);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [fit]);

  // Masque la flèche d'incitation dès que l'utilisateur a commencé à swiper.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const onScroll = () => {
      if (grid.scrollLeft > 8) setHasSwiped(true);
    };
    grid.addEventListener("scroll", onScroll, { passive: true });
    return () => grid.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    api
      .get("/practice/alphabet/mastery")
      .then((r) => setMastered(r.data?.mastered || []))
      .catch(() => setMastered([]));
  }, []);

  const stats = useMemo(() => {
    const done = new Set(mastered);
    return ALPHABET_MODULES.map((m) => {
      const ok = m.remote
        ? [...done].filter((c) => !staticChars.has(c)).length
        : moduleChars(m).filter((c) => done.has(c)).length;
      const total = m.total || 1;
      return {
        ok: Math.min(ok, total),
        pct: Math.round((100 * Math.min(ok, total)) / total),
      };
    });
  }, [mastered]);

  const open = (m, i) => {
    const prev = i > 0 ? ALPHABET_MODULES[i - 1] : null;
    if (prev && stats[i - 1].pct < UNLOCK_PCT) {
      setGate({ mod: m, prev });
      return;
    }
    navigate(`/practice/alphabet/${m.slug}`);
  };

  return (
    <>
      <style>{`
        .alpha-container {
          position: fixed;
          top: var(--nav-top-mobile, 4rem);
          bottom: var(--nav-bottom-mobile, 4rem);
          left: 0;
          right: 0;
          z-index: 10;
          display: flex;
          overflow-y: auto; 
          overflow-x: hidden;
        }
        @media (min-width: 768px) {
          .alpha-container {
            top: var(--nav-top-desktop, 5.5rem);
            bottom: 1.25rem;
            padding: 0 clamp(1rem, 2.5vw, 2rem);
            justify-content: center;
          }
        }

        .alpha-card {
          width: 100%;
          min-height: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          container-type: inline-size;
        }
        @media (min-width: 768px) {
          .alpha-card { max-width: 1100px; justify-content: flex-start; }
        }

        .a-inner {
          --u: 10px;
          width: 100%;
          padding: calc(var(--u) * 1) calc(var(--u) * 1.6);
        }

        .a-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: calc(var(--u) * 1);
          margin-bottom: calc(var(--u) * 1.2);
        }
        .a-header-left {
          display: flex;
          align-items: flex-start;
          gap: calc(var(--u) * 0.8);
          min-width: 0;
        }
        .a-back-btn {
          margin-top: calc(var(--u) * 0.2);
          flex-shrink: 0;
          border-radius: calc(var(--u) * 1.2);
          border-width: 1px;
          padding: calc(var(--u) * 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.15s;
        }
        .a-back-btn:hover { background-color: var(--secondary); }
        .a-title-group { min-width: 0; }
        .a-title {
          font-size: calc(var(--u) * 2.4);
          font-weight: 700;
          line-height: 1.15;
        }
        .a-subtitle {
          font-size: calc(var(--u) * 1.35);
          color: var(--muted-foreground);
          margin-top: calc(var(--u) * 0.2);
        }
        .a-info-btn {
          flex-shrink: 0;
          border-radius: calc(var(--u) * 1.2);
          border-width: 1px;
          padding: calc(var(--u) * 0.8) calc(var(--u) * 1.2);
          font-size: calc(var(--u) * 1.1);
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: calc(var(--u) * 0.6);
        }

        /* ── Grille des modules ── */
        .a-grid-wrap {
          position: relative;
        }
        .a-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: calc(var(--u) * 0.8);
        }
        @media (min-width: 640px) {
          .a-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .a-grid { grid-template-columns: repeat(4, 1fr); }
        }

        /* Sur mobile : carrousel horizontal à snap, une carte à la fois,
           avec un aperçu des cartes voisines pour inciter à swiper. */
        @media (max-width: 767px) {
          .a-grid {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            gap: calc(var(--u) * 1);
            margin: 0 calc(var(--u) * -1.6);
            padding: calc(var(--u) * 0.2) calc(9% + var(--u) * 0.5);
          }
          .a-grid::-webkit-scrollbar { display: none; }

          .a-mod {
            flex: 0 0 82%;
            min-width: 0;
            scroll-snap-align: center;
            scroll-snap-stop: always;
          }
        }

        .a-mod {
          position: relative;
          min-height: 0;
          border-radius: calc(var(--u) * 1.4);
          border-width: 2px;
          padding: calc(var(--u) * 1.2);
          text-align: left;
          display: flex;
          flex-direction: column;
          transition: border-color 0.15s, transform 0.15s;
        }
        .a-mod.active:hover {
          border-color: rgba(var(--primary-rgb), 0.5);
          transform: translateY(-2px);
        }
        .a-mod-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: calc(var(--u) * 1);
          margin-bottom: calc(var(--u) * 0.6);
          flex-shrink: 0;
        }
        .a-mod-icon {
          width: calc(var(--u) * 3);
          height: calc(var(--u) * 3);
          border-radius: 999px;
          display: grid;
          place-items: center;
          font-size: calc(var(--u) * 1.3);
          font-weight: 700;
        }
        .a-mod-locked {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: calc(var(--u) * 0.15);
          font-size: calc(var(--u) * 0.85);
          font-weight: 700;
          text-transform: uppercase;
          color: var(--muted-foreground);
          text-align: right;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .a-mod-arabic {
          font-size: calc(var(--u) * 2.4);
          color: var(--muted-foreground);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .a-mod-title {
          font-size: calc(var(--u) * 1.7);
          font-weight: 700;
          line-height: 1.15;
          margin-bottom: calc(var(--u) * 0.2);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: calc(var(--u) * 1.7 * 1.15 * 2);
        }
        .a-mod-tagline {
          font-size: calc(var(--u) * 1.25);
          color: var(--muted-foreground);
          margin-bottom: calc(var(--u) * 0.6);
        }
        .a-mod-intro {
          font-size: calc(var(--u) * 1.15);
          color: var(--muted-foreground);
          line-height: 1.3;
          margin-bottom: calc(var(--u) * 1);
        }
        .a-mod-footer {
          margin-top: auto;
        }
        .a-mod-stat {
          font-size: calc(var(--u) * 1.25);
          font-weight: 600;
          color: var(--muted-foreground);
          margin-bottom: calc(var(--u) * 0.5);
          font-variant-numeric: tabular-nums;
        }
        .a-mod-bar-bg {
          height: calc(var(--u) * 0.7);
          border-radius: 999px;
          background-color: var(--secondary);
          overflow: hidden;
          margin-bottom: calc(var(--u) * 0.7);
        }
        .a-mod-bar-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.3s ease;
        }
        .a-mod-status {
          font-size: calc(var(--u) * 1.25);
          font-weight: 700;
          color: var(--primary);
          display: flex;
          align-items: center;
          gap: calc(var(--u) * 0.4);
        }

        /* ── Flèche d'incitation au swipe (mobile uniquement) ── */
        .a-swipe-hint {
          display: none;
        }
        @media (max-width: 767px) {
          .a-swipe-hint {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            right: calc(var(--u) * 0.4);
            top: 50%;
            width: calc(var(--u) * 2.8);
            height: calc(var(--u) * 2.8);
            margin-top: calc(var(--u) * -1.4);
            border-radius: 999px;
            background: var(--primary);
            color: white;
            box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
            pointer-events: none;
            animation: a-swipe-pulse 1.3s ease-in-out infinite;
            z-index: 5;
          }
        }
        @keyframes a-swipe-pulse {
          0%, 100% { transform: translateX(0); opacity: 1; }
          50% { transform: translateX(7px); opacity: 0.55; }
        }
      `}</style>

      <div className="alpha-container bg-background md:bg-transparent">
        <div className="alpha-card" ref={cardRef}>
          <div className="a-inner" ref={innerRef}>
            <div className="a-header">
              <div className="a-header-left">
                <button
                  onClick={() => navigate("/practice")}
                  data-testid="back-practice"
                  className="a-back-btn bg-card"
                  aria-label={t("alphabetmodules.retour")}
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="a-title-group">
                  <h1 className="a-title font-head">
                    {t("alphabetmodules.apprendre_a_lire")}
                  </h1>
                  <p className="a-subtitle">
                    {t("alphabetmodules.quatre_etapes_dans_l_ordre")}
                  </p>
                </div>
              </div>
              <button
                data-testid="alphabet-info"
                onClick={() => setShowInfo(true)}
                className="a-info-btn bg-card"
              >
                <HelpCircle size={15} className="text-primary" />
                <span>{t("alphabetmodules.a_quoi_ca_sert")}</span>
              </button>
            </div>

            <div className="a-grid-wrap">
              <div className="a-grid" ref={gridRef}>
                {ALPHABET_MODULES.map((m, i) => (
                  <ModuleCard
                    key={m.slug}
                    mod={m}
                    stat={stats[i]}
                    locked={i > 0 && stats[i - 1].pct < UNLOCK_PCT}
                    onClick={() => open(m, i)}
                  />
                ))}
              </div>
              {!hasSwiped && (
                <div className="a-swipe-hint" aria-hidden="true">
                  <ChevronRight size={18} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}
      {gate && (
        <GateModal
          mod={gate.mod}
          prev={gate.prev}
          onCancel={() => setGate(null)}
          onConfirm={() => {
            const slug = gate.mod.slug;
            setGate(null);
            navigate(`/practice/alphabet/${slug}`);
          }}
        />
      )}
    </>
  );
}

function ModuleCard({ mod, stat, locked, onClick }) {
  const started = stat.ok > 0;
  const finished = stat.pct >= 100;
  const status = finished
    ? t("alphabetmodules.termine")
    : started
      ? t("alphabetmodules.en_cours")
      : t("alphabetmodules.commencer");

  return (
    <button
      data-testid={`alpha-module-${mod.slug}`}
      onClick={onClick}
      className={`a-mod ${locked ? "opacity-70" : "active"} ${
        finished ? "border-emerald-500/50 bg-emerald-500/5" : "bg-card"
      }`}
    >
      <div className="a-mod-header">
        <span
          className={`a-mod-icon ${
            finished
              ? "bg-emerald-500 text-white"
              : "bg-primary text-primary-foreground"
          }`}
        >
          {finished ? <Check size={16} /> : mod.n}
        </span>
        {locked ? (
          <span className="a-mod-locked">
            <Lock size={11} /> {t("alphabetmodules.conseille_apres")}
          </span>
        ) : (
          <span className="a-mod-arabic arabic" dir="rtl">
            {mod.sample}
          </span>
        )}
      </div>

      <h2 className="a-mod-title font-head">{mod.title}</h2>
      <p className="a-mod-tagline">{mod.tagline}</p>

      <p className="a-mod-intro">{mod.intro}</p>

      <div className="a-mod-footer">
        <div className="a-mod-stat">
          {stat.ok} / {mod.total}
        </div>
        <div className="a-mod-bar-bg">
          <div
            className={`a-mod-bar-fill ${finished ? "bg-emerald-500" : "bg-primary"}`}
            style={{ width: `${stat.pct}%` }}
          />
        </div>
        <span className="a-mod-status">
          {status} <ArrowRight size={15} />
        </span>
      </div>
    </button>
  );
}

function InfoModal({ onClose }) {
  return (
    <Overlay onClose={onClose}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-head text-lg font-bold">
          {t("alphabetmodules.pourquoi_quatre_modules")}
        </h3>
        <button
          onClick={onClose}
          className="rounded-full p-1.5 hover:bg-muted shrink-0"
        >
          <X size={17} />
        </button>
      </div>
      <div className="overflow-y-auto max-h-[60vh] pr-1 space-y-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t("alphabetmodules.lire_l_arabe_ne_demande_pas")}
        </p>
        {ALPHABET_MODULES.map((m) => (
          <div key={m.slug} className="rounded-xl bg-secondary/50 p-3">
            <div className="text-sm font-bold mb-1">
              {m.n}. {m.title}{" "}
              <span className="font-normal text-muted-foreground">
                · {t("alphabetmodules.nb_elements", { count: m.total })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {m.why}
            </p>
          </div>
        ))}
        <p className="text-xs text-muted-foreground leading-relaxed">
          {t("alphabetmodules.deblocage_regle", { pct: UNLOCK_PCT })}
        </p>
      </div>
      <button
        onClick={onClose}
        className="w-full mt-4 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
      >
        {t("exerciseblock.j_ai_compris")}
      </button>
    </Overlay>
  );
}

function GateModal({ mod, prev, onCancel, onConfirm }) {
  return (
    <Overlay onClose={onCancel}>
      <div className="text-center">
        <AlertTriangle size={34} className="mx-auto mb-3 text-amber-500" />
        <h3 className="font-head text-lg font-bold mb-2">
          {t("alphabetmodules.titre_gate", { n: prev.n })}
        </h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {t("alphabetmodules.message_gate", {
            modTitle: mod.title,
            prevTitle: prev.title,
            pct: UNLOCK_PCT,
          })}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onCancel}
            className="py-3 rounded-2xl border font-semibold"
          >
            {t("groupui.annuler")}
          </button>
          <button
            data-testid="gate-confirm"
            onClick={onConfirm}
            className="py-3 rounded-2xl bg-primary text-primary-foreground font-semibold"
          >
            {t("exerciseblock.continuer")}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl border bg-card p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
